"use client";
import { useCallback, useEffect, useState } from "react"
import { Fragment } from "@tiptap/pm/model"
import { NodeSelection, TextSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Icons ---
import { ListIcon } from "@/tiptap/icons/list-icon"
import { ListOrderedIcon } from "@/tiptap/icons/list-ordered-icon"
import { ListTodoIcon } from "@/tiptap/icons/list-todo-icon"

// --- Lib ---
import {
  FOCUS_NO_SCROLL,
  findConvertibleAncestor,
  findNodePosition,
  getSelectedBlockNodes,
  isNodeInSchema,
  isNodeTypeSelected,
  isValidPosition,
  runWithPreservedWindowScroll,
  selectionWithinConvertibleTypes,
} from "@/tiptap/lib/tiptap-utils";

export const listIcons = {
  bulletList: ListIcon,
  orderedList: ListOrderedIcon,
  taskList: ListTodoIcon,
}

export const listLabels = {
  bulletList: "Bullet List",
  orderedList: "Ordered List",
  taskList: "Task List",
}

export const LIST_SHORTCUT_KEYS = {
  bulletList: "mod+shift+8",
  orderedList: "mod+shift+7",
  taskList: "mod+shift+9",
}

const CONVERTIBLE_LIST_TYPES = new Set(["bulletList", "orderedList", "taskList"])
const LIST_ITEM_TYPES = new Set(["listItem", "taskItem"])
const LIST_ITEM_TYPE_BY_LIST_TYPE = {
  bulletList: "listItem",
  orderedList: "listItem",
  taskList: "taskItem",
}

const filterAttrsForNodeType = (nodeType, attrs = {}) => {
  return Object.keys(nodeType.attrs).reduce((filteredAttrs, name) => {
    if (attrs[name] !== undefined) {
      filteredAttrs[name] = attrs[name]
    }

    return filteredAttrs
  }, {})
}

const getSelectedListNodes = (state) => {
  const { doc, selection } = state
  const selectedLists = []
  const seen = new Set()

  const addList = (node, pos) => {
    if (!CONVERTIBLE_LIST_TYPES.has(node.type.name) || seen.has(pos)) return

    seen.add(pos)
    selectedLists.push({ node, pos })
  }

  selection.ranges.forEach(({ $from, $to }) => {
    for (let depth = $from.depth; depth > 0; depth--) {
      addList($from.node(depth), $from.before(depth))
    }

    for (let depth = $to.depth; depth > 0; depth--) {
      addList($to.node(depth), $to.before(depth))
    }

    doc.nodesBetween($from.pos, $to.pos, addList)
  })

  return selectedLists
}

const getTopLevelListNodes = (listNodes) => {
  return listNodes.filter(({ node, pos }) => {
    return !listNodes.some(({ node: otherNode, pos: otherPos }) => {
      return otherPos < pos && pos < otherPos + otherNode.nodeSize
    })
  })
}

const findNodePos = (doc, targetNode) => {
  let foundPos = null

  doc.descendants((node, pos) => {
    if (node === targetNode) {
      foundPos = pos
      return false
    }

    return true
  })

  return foundPos
}

const convertNodeTreeToListType = (node, schema, listTypeName) => {
  if (node.isText) return node

  const targetListType = schema.nodes[listTypeName]
  const targetItemType = schema.nodes[LIST_ITEM_TYPE_BY_LIST_TYPE[listTypeName]]
  if (!targetListType || !targetItemType) return node

  const childNodes = []
  node.forEach((child) => {
    childNodes.push(convertNodeTreeToListType(child, schema, listTypeName))
  })

  const content = childNodes.length > 0
    ? Fragment.fromArray(childNodes)
    : node.content

  if (CONVERTIBLE_LIST_TYPES.has(node.type.name)) {
    return targetListType.create(
      filterAttrsForNodeType(targetListType, node.attrs),
      content,
      node.marks,
    )
  }

  if (LIST_ITEM_TYPES.has(node.type.name)) {
    return targetItemType.create(
      filterAttrsForNodeType(targetItemType, node.attrs),
      content,
      node.marks,
    )
  }

  if (childNodes.length === 0) return node

  return node.copy(content)
}

const getSelectedListItems = (state, type) => {
  const { doc, selection } = state
  const candidates = []
  const seen = new Set()
  const parentPositions = new Map()

  const getParentPos = (parent) => {
    if (!parentPositions.has(parent)) {
      parentPositions.set(parent, findNodePos(doc, parent))
    }

    return parentPositions.get(parent)
  }

  const addItem = (node, pos, parent, index) => {
    if (
      !node ||
      !parent ||
      !LIST_ITEM_TYPES.has(node.type.name) ||
      !CONVERTIBLE_LIST_TYPES.has(parent.type.name) ||
      seen.has(pos)
    ) {
      return
    }

    const parentPos = getParentPos(parent)
    if (!isValidPosition(parentPos)) return

    seen.add(pos)
    candidates.push({ node, pos, parent, parentPos, index })
  }

  const addNearestItemAncestor = ($pos) => {
    for (let depth = $pos.depth; depth > 0; depth--) {
      const node = $pos.node(depth)
      if (!LIST_ITEM_TYPES.has(node.type.name)) continue

      addItem(node, $pos.before(depth), $pos.node(depth - 1), $pos.index(depth - 1))
      return
    }
  }

  selection.ranges.forEach(({ $from, $to }) => {
    doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
      if (!node.isText) return true

      const textStart = pos
      const textEnd = pos + node.nodeSize
      if (textEnd <= $from.pos || textStart >= $to.pos) return true

      addNearestItemAncestor(doc.resolve(Math.max(textStart, $from.pos)))
      return true
    })
  })

  return candidates.filter(({ node, pos }) => {
    return !candidates.some(({ node: otherNode, parent: otherParent, pos: otherPos }) => {
      return (
        otherParent.type.name !== type &&
        otherPos < pos &&
        pos + node.nodeSize <= otherPos + otherNode.nodeSize
      )
    })
  })
}

const createListNode = (listType, attrs, items, marks) => {
  return listType.create(
    filterAttrsForNodeType(listType, attrs),
    Fragment.fromArray(items),
    marks,
  )
}

const buildPartiallyConvertedLists = (listNode, selectedIndexes, schema, type) => {
  const targetListType = schema.nodes[type]
  if (!targetListType) return []

  const listNodes = []
  let runItems = []
  let runShouldConvert = null

  const flushRun = () => {
    if (runItems.length === 0) return

    const listType = runShouldConvert ? targetListType : listNode.type
    const items = runShouldConvert
      ? runItems.map((item) => convertNodeTreeToListType(item, schema, type))
      : runItems

    listNodes.push(createListNode(listType, listNode.attrs, items, listNode.marks))
    runItems = []
  }

  listNode.forEach((child, _offset, index) => {
    const shouldConvert = selectedIndexes.has(index)

    if (runShouldConvert !== shouldConvert) {
      flushRun()
      runShouldConvert = shouldConvert
    }

    runItems.push(child)
  })

  flushRun()

  return listNodes
}

const convertRangeSelectedListTypes = (editor, type) => {
  if (!CONVERTIBLE_LIST_TYPES.has(type)) return false

  const { state, view } = editor
  const selectedItems = getSelectedListItems(state, type)
  const selectedItemsByParent = new Map()

  selectedItems.forEach((item) => {
    if (item.parent.type.name === type) return

    const group = selectedItemsByParent.get(item.parentPos) ?? {
      node: item.parent,
      pos: item.parentPos,
      indexes: new Set(),
    }

    group.indexes.add(item.index)
    selectedItemsByParent.set(item.parentPos, group)
  })

  if (selectedItemsByParent.size === 0) return false

  let tr = state.tr

  Array.from(selectedItemsByParent.values())
    .sort((a, b) => b.pos - a.pos)
    .forEach(({ node, pos, indexes }) => {
      const listNodes = buildPartiallyConvertedLists(node, indexes, state.schema, type)
      if (listNodes.length === 0) return

      tr = tr.replaceWith(
        pos,
        pos + node.nodeSize,
        Fragment.fromArray(listNodes),
      )
    })

  if (!tr.docChanged) return false

  view.dispatch(tr)
  editor.commands.focus(undefined, FOCUS_NO_SCROLL)

  return true
}

const hasSelectedListTypeConversion = (state, type) => {
  if (!CONVERTIBLE_LIST_TYPES.has(type)) return false

  return getSelectedListNodes(state).some(({ node }) => node.type.name !== type)
}

const findInnermostConvertibleList = ($pos) => {
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const node = $pos.node(depth)
    if (CONVERTIBLE_LIST_TYPES.has(node.type.name)) {
      return { node, pos: $pos.before(depth) }
    }
  }

  return null
}

const replaceListWithType = (editor, listNode, pos, type) => {
  const { state, view } = editor
  const converted = convertNodeTreeToListType(listNode, state.schema, type)
  const tr = state.tr.replaceWith(pos, pos + listNode.nodeSize, converted)

  if (!tr.docChanged) return false

  view.dispatch(tr)
  editor.commands.focus(undefined, FOCUS_NO_SCROLL)
  return true
}

const convertInnermostListAtCursor = (editor, type) => {
  if (!CONVERTIBLE_LIST_TYPES.has(type)) return false

  const list = findInnermostConvertibleList(editor.state.selection.$from)
  if (!list || list.node.type.name === type) return false

  return replaceListWithType(editor, list.node, list.pos, type)
}

const convertSelectedListTypes = (editor, type) => {
  if (!CONVERTIBLE_LIST_TYPES.has(type)) return false

  const { state } = editor
  const targetType = state.schema.nodes[type]
  if (!targetType) return false
  if (!(state.selection instanceof NodeSelection)) {
    return convertRangeSelectedListTypes(editor, type)
  }

  if (CONVERTIBLE_LIST_TYPES.has(state.selection.node.type.name)) {
    if (state.selection.node.type.name === type) return false

    return replaceListWithType(
      editor,
      state.selection.node,
      state.selection.from,
      type,
    )
  }

  const selectedLists = getTopLevelListNodes(getSelectedListNodes(state))
    .filter(({ node }) => node.type.name !== type)

  if (selectedLists.length === 0) return false

  let tr = state.tr

  selectedLists
    .sort((a, b) => b.pos - a.pos)
    .forEach(({ node, pos }) => {
      tr = tr.replaceWith(
        pos,
        pos + node.nodeSize,
        convertNodeTreeToListType(node, state.schema, type),
      )
    })

  if (!tr.docChanged) return false

  editor.view.dispatch(tr)
  editor.commands.focus(undefined, FOCUS_NO_SCROLL)

  return true
}

/**
 * Checks if a list can be toggled in the current editor state
 */
export function canToggleList(editor, type, turnInto = true) {
  if (!editor || !editor.isEditable) return false
  if (!isNodeInSchema(type, editor) || isNodeTypeSelected(editor, ["image"]))
    return false

  if (!turnInto) {
    switch (type) {
      case "bulletList":
        return editor.can().toggleBulletList();
      case "orderedList":
        return editor.can().toggleOrderedList();
      case "taskList":
        return editor.can().toggleList("taskList", "taskItem");
      default:
        return false
    }
  }

  // Ensure selection is in nodes we're allowed to convert
  if (
    !selectionWithinConvertibleTypes(editor, [
      "paragraph",
      "heading",
      "bulletList",
      "orderedList",
      "taskList",
      "blockquote",
      "callout",
      "codeBlock",
    ])
  )
    return false

  // Either we can set list directly on the selection,
  // or we can clear formatting/nodes to arrive at a list.
  switch (type) {
    case "bulletList":
      return hasSelectedListTypeConversion(editor.state, type) || editor.can().toggleBulletList() || editor.can().clearNodes();
    case "orderedList":
      return hasSelectedListTypeConversion(editor.state, type) || editor.can().toggleOrderedList() || editor.can().clearNodes();
    case "taskList":
      return (hasSelectedListTypeConversion(editor.state, type) || editor.can().toggleList("taskList", "taskItem") || editor.can().clearNodes());
    default:
      return false
  }
}

/**
 * Checks if list is currently active
 */
export function isListActive(editor, type) {
  if (!editor || !editor.isEditable) return false

  switch (type) {
    case "bulletList":
      return editor.isActive("bulletList");
    case "orderedList":
      return editor.isActive("orderedList");
    case "taskList":
      return editor.isActive("taskList");
    default:
      return false
  }
}

/**
 * Toggles list in the editor
 */
export function toggleList(editor, type) {
  if (!editor || !editor.isEditable) return false
  if (!canToggleList(editor, type)) return false

  return runWithPreservedWindowScroll(() => {
    try {
      const view = editor.view
      let state = view.state
      let tr = state.tr

      const blocks = getSelectedBlockNodes(editor)

      // In case a selection contains multiple blocks, we only allow
      // toggling to nide if there's exactly one block selected
      // we also dont block the canToggle since it will fall back to the bottom logic
      const isPossibleToTurnInto =
        selectionWithinConvertibleTypes(editor, [
          "paragraph",
          "heading",
          "bulletList",
          "orderedList",
          "taskList",
          "blockquote",
          "callout",
          "codeBlock",
        ]) && blocks.length === 1

      if (state.selection.empty && convertInnermostListAtCursor(editor, type)) {
        return true
      }

      // No selection, find the the cursor position
      if (state.selection.empty && isPossibleToTurnInto) {
        const pos = findNodePosition({
          editor,
          node: findConvertibleAncestor(state.selection),
        })?.pos
        if (!isValidPosition(pos)) return false

        tr = tr.setSelection(NodeSelection.create(state.doc, pos))
        view.dispatch(tr)
        state = view.state
      }

      const selection = state.selection

      let chain = editor.chain().focus(undefined, FOCUS_NO_SCROLL)

      if (convertSelectedListTypes(editor, type)) {
        return true
      }

      // Handle NodeSelection
      if (selection instanceof NodeSelection) {
        const firstChild = selection.node.firstChild?.firstChild
        const lastChild = selection.node.lastChild?.lastChild

        const from = firstChild
          ? selection.from + firstChild.nodeSize
          : selection.from + 1

        const to = lastChild
          ? selection.to - lastChild.nodeSize
          : selection.to - 1

        const resolvedFrom = state.doc.resolve(from)
        const resolvedTo = state.doc.resolve(to)

        chain = chain
          .setTextSelection(TextSelection.between(resolvedFrom, resolvedTo))
          .clearNodes()
      }

      if (editor.isActive(type)) {
        // Unwrap list
        chain
          .liftListItem("listItem")
          .lift("bulletList")
          .lift("orderedList")
          .lift("taskList")
          .run()
      } else {
        // Wrap in specific list type
        const toggleMap = {
          bulletList: () => chain.toggleBulletList(),
          orderedList: () => chain.toggleOrderedList(),
          taskList: () => chain.toggleList("taskList", "taskItem"),
        }

        const toggle = toggleMap[type]
        if (!toggle) return false

        toggle().run()
      }

      editor.chain().focus(undefined, FOCUS_NO_SCROLL).selectTextblockEnd().run()

      return true
    } catch {
      return false
    }
  })
}

/**
 * Determines if the list button should be shown
 */
export function shouldShowButton(props) {
  const { editor, type, hideWhenUnavailable } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!isNodeInSchema(type, editor)) return false

  if (!editor.isActive("code")) {
    return canToggleList(editor, type);
  }

  return true
}

/**
 * Custom hook that provides list functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MySimpleListButton() {
 *   const { isVisible, handleToggle, isActive } = useList({ type: "bulletList" })
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleToggle}>Bullet List</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedListButton() {
 *   const { isVisible, handleToggle, label, isActive } = useList({
 *     type: "orderedList",
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onToggled: () => console.log('List toggled!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleToggle}
 *       aria-label={label}
 *       aria-pressed={isActive}
 *     >
 *       Toggle List
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useList(config) {
  const {
    editor: providedEditor,
    type,
    hideWhenUnavailable = false,
    onToggled,
  } = config

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)
  const canToggle = canToggleList(editor, type)
  const isActive = isListActive(editor, type)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, type, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, type, hideWhenUnavailable])

  const handleToggle = useCallback(() => {
    if (!editor) return false

    const success = toggleList(editor, type)
    if (success) {
      onToggled?.()
    }
    return success
  }, [editor, type, onToggled])

  return {
    isVisible,
    isActive,
    handleToggle,
    canToggle,
    label: listLabels[type],
    shortcutKeys: LIST_SHORTCUT_KEYS[type],
    Icon: listIcons[type],
  }
}
